const SELECTOR = {
  CONTAINER: '[data-bundle-options]',
  SELECT: '[data-bundle-option]',
  SELECTION: '[data-bundle-selection]',
  STATUS: '[data-bundle-status]',
  STATUS_TEXT: '[data-bundle-status-text]',
  MIRROR: '[data-bundle-options-mirror]',
};

const BUNDLE_SELECTION_SEPARATOR = ' <> ';

const STATUS_CLASSES = [
  'inventory-status--in-stock',
  'inventory-status--low',
  'inventory-status--pre-order',
  'inventory-status--sold-out',
];

elabs.ProductBundleOptions = class {
  constructor(element) {
    this.view = element;
    this.selectedValues = new Map();
    this.wireEvents();
    this.rememberSelectedValues();
    this.updateStatus();
  }

  get selects() {
    return Array.from(this.view.querySelectorAll(SELECTOR.SELECT));
  }

  get selectionInput() {
    return this.view.querySelector(SELECTOR.SELECTION);
  }

  wireEvents = () => {
    this.view.addEventListener('change', this.handleChange);

    // variant-selects replaces the inner HTML of the container when the variant changes,
    // so the previous choices are restored whenever the new selectors are rendered.
    new MutationObserver(this.handleRender).observe(this.view, { childList: true });

    // The product forms use "novalidate" and submit via fetch, so native "required"
    // validation never runs. We listen on the document in the capture phase so this
    // runs before the theme's own submit handler and can block an invalid submission.
    // The sticky buy bar has its own form that doesn't contain the fields, so on that
    // form we also mirror the values into it before it is serialized.
    document.addEventListener('submit', this.handleFormSubmit, true);
  };

  handleChange = (event) => {
    if (!event.target.matches(SELECTOR.SELECT)) {
      return;
    }

    this.rememberSelectedValues();
    this.updateSelectionInput();
    this.updateStatus();
  };

  handleRender = () => {
    this.selects.forEach((select) => {
      const previousValue = this.selectedValues.get(select.name);
      const previousOption = Array.from(select.options).find((option) => option.value === previousValue);

      if (previousOption && !previousOption.disabled && previousOption.dataset.unavailable === undefined) {
        select.value = previousValue;
      }
    });

    const addButton = this.getAddButton();
    if (addButton) {
      delete addButton.dataset.bundleUnavailable;
    }

    this.rememberSelectedValues();
    this.updateSelectionInput();
    this.updateStatus();
  };

  getAddButton = () => {
    const productFormId = this.selectionInput?.getAttribute('form');
    return productFormId ? document.getElementById(productFormId)?.querySelector('[name="add"]') : null;
  };

  rememberSelectedValues = () => {
    this.selects.forEach((select) => {
      if (select.value) {
        this.selectedValues.set(select.name, select.value);
      }
    });
  };

  updateSelectionInput = () => {
    const selectionInput = this.selectionInput;
    if (!selectionInput) {
      return;
    }

    selectionInput.value = this.selects.map((select) => select.value).join(BUNDLE_SELECTION_SEPARATOR);
  };

  getSelectionStock = () => {
    let latestPreOrderTimestamp = 0;
    let preOrderMessage = '';
    let lowestQuantity = null;
    let isUnavailable = false;

    this.selects.forEach((select) => {
      const dataset = select.selectedOptions[0]?.dataset || {};

      if (dataset.unavailable !== undefined) {
        isUnavailable = true;
      }

      if (dataset.preOrderMessage) {
        const timestamp = Number(dataset.preOrderTimestamp) || 0;
        if (timestamp >= latestPreOrderTimestamp) {
          latestPreOrderTimestamp = timestamp;
          preOrderMessage = dataset.preOrderMessage;
        }
      }

      if (dataset.quantity !== undefined) {
        const quantity = Number(dataset.quantity);
        if (lowestQuantity === null || quantity < lowestQuantity) {
          lowestQuantity = quantity;
        }
      }
    });

    return { preOrderMessage, lowestQuantity, isUnavailable };
  };

  updateStatus = () => {
    const { preOrderMessage, lowestQuantity, isUnavailable } = this.getSelectionStock();
    this.updateStockStatus(preOrderMessage, lowestQuantity, isUnavailable);
    this.updateAddButton(Boolean(preOrderMessage), isUnavailable);
  };

  updateStockStatus = (preOrderMessage, lowestQuantity, isUnavailable) => {
    const status = this.view.querySelector(SELECTOR.STATUS);
    const statusText = status?.querySelector(SELECTOR.STATUS_TEXT);
    if (!status || !statusText) {
      return;
    }

    const { dataset } = status;
    const threshold = Number(dataset.threshold) || 0;
    const showQuantity = dataset.showQuantityInStock === 'true';
    let statusClass = 'inventory-status--in-stock';
    let statusMessage = dataset.inStockMessage;

    if (isUnavailable) {
      statusClass = 'inventory-status--sold-out';
      statusMessage = dataset.outOfStockMessage;
    } else if (preOrderMessage) {
      statusClass = 'inventory-status--pre-order';
      statusMessage = preOrderMessage;
    } else if (lowestQuantity !== null && lowestQuantity > threshold) {
      if (showQuantity) {
        statusMessage = dataset.inStockQuantityMessage.replace('[quantity]', lowestQuantity);
      }
    } else if (lowestQuantity !== null && lowestQuantity > 0) {
      statusClass = 'inventory-status--low';
      statusMessage = showQuantity
        ? dataset.fewLeftQuantityMessage.replace('[quantity]', lowestQuantity)
        : dataset.fewLeftMessage;
    } else if (lowestQuantity !== null) {
      statusClass = 'inventory-status--sold-out';
      statusMessage = dataset.outOfStockMessage;
    }

    status.classList.remove(...STATUS_CLASSES);
    status.classList.add(statusClass);
    statusText.textContent = statusMessage;
  };

  updateAddButton = (isPreOrder, isUnavailable) => {
    const sectionId = this.view.dataset.sectionId;
    const variantSelects = document.getElementById(`variant-selects-${sectionId}`);
    if (variantSelects) {
      variantSelects.dataset.isPreorder = String(isPreOrder);
    }

    const addButton = this.getAddButton();
    const addButtonText = addButton?.querySelector(':scope > span');
    if (!addButtonText) {
      return;
    }

    const isDisabledByBundle = addButton.dataset.bundleUnavailable === 'true';
    if (addButton.disabled && !isDisabledByBundle) {
      return;
    }

    if (isUnavailable) {
      addButton.setAttribute('disabled', 'disabled');
      addButton.dataset.bundleUnavailable = 'true';
      addButtonText.textContent = window.variantStrings.soldOut;
      return;
    }

    if (isDisabledByBundle) {
      addButton.removeAttribute('disabled');
      delete addButton.dataset.bundleUnavailable;
    }

    addButtonText.textContent = isPreOrder ? window.variantStrings.preOrder : window.variantStrings.addToCart;
  };

  handleFormSubmit = (event) => {
    const submittedForm = event.target;
    const mainFormId = this.selectionInput?.getAttribute('form');
    if (!mainFormId || !(submittedForm instanceof HTMLFormElement)) {
      return;
    }

    // Use getAttribute('id') rather than .id: the product form contains an
    // <input name="id">, which clobbers the form's .id property with that element.
    const submittedFormId = submittedForm.getAttribute('id');
    const isMainForm = submittedFormId === mainFormId;
    const isBuyBarForm = submittedFormId === `${mainFormId}-product-buy-bar`;
    if (!isMainForm && !isBuyBarForm) {
      return;
    }

    const invalidSelect = this.selects.find(
      (select) => !select.checkValidity() || select.selectedOptions[0]?.dataset.unavailable !== undefined,
    );
    if (invalidSelect) {
      event.preventDefault();
      event.stopImmediatePropagation();
      invalidSelect.reportValidity();
      invalidSelect.focus();
      return;
    }

    this.updateSelectionInput();

    if (isBuyBarForm) {
      this.syncValuesToBuyBar(submittedForm);
    }
  };

  syncValuesToBuyBar = (buyBarForm) => {
    buyBarForm.querySelectorAll(SELECTOR.MIRROR).forEach((mirror) => mirror.remove());

    [...this.selects, this.selectionInput].forEach((input) => {
      const mirror = document.createElement('input');
      mirror.type = 'hidden';
      mirror.setAttribute('data-bundle-options-mirror', '');
      mirror.setAttribute('name', input.getAttribute('name'));
      mirror.value = input.value;
      buyBarForm.appendChild(mirror);
    });
  };
};

const initProductBundleOptions = () => {
  document.querySelectorAll(SELECTOR.CONTAINER).forEach((element) => {
    if (element.dataset.initialized === 'true') {
      return;
    }
    element.dataset.initialized = 'true';
    void new elabs.ProductBundleOptions(element);
  });
};

initProductBundleOptions();

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initProductBundleOptions);
}
